import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { agenciesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import Select from '../components/Select';
import './AgencyView.css';

const statusOptions = [
  { value: 'pending', label: 'Pending', name: 'status' },
  { value: 'approved', label: 'Approved', name: 'status' },
  { value: 'rejected', label: 'Rejected', name: 'status' },
  { value: 'suspended', label: 'Suspended', name: 'status' }
];

const AgencyView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [agency, setAgency] = useState(null);
  const [error, setError] = useState('');
  const [statusUpdating, setStatusUpdating] = useState(false);

  useEffect(() => {
    fetchAgency();
  }, [id]);

  const fetchAgency = async () => {
    try {
      setLoading(true);
      const response = await agenciesAPI.getById(id);
      if (response.data.success) {
        setAgency(response.data.data.agency);
      }
    } catch (error) {
      console.error('Failed to fetch agency:', error);
      setError('Failed to load agency details');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    try {
      setStatusUpdating(true);
      await agenciesAPI.updateStatus(id, { status: newStatus });
      setAgency(prev => ({ ...prev, status: newStatus }));
    } catch (error) {
      console.error('Failed to update status:', error);
      setError('Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this agency?')) {
      return;
    }

    try {
      await agenciesAPI.delete(id);
      navigate('/agencies');
    } catch (error) {
      console.error('Failed to delete agency:', error);
      setError('Failed to delete agency');
    }
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      approved: 'status-approved',
      rejected: 'status-rejected',
      suspended: 'status-suspended'
    };
    return statusClasses[status] || 'status-pending';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="dashboard-main">
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading agency...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error && !agency) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="dashboard-main">
          <div className="error-state">
            <p>{error}</p>
            <button className="btn-primary" onClick={() => navigate('/agencies')}>
              Back to Agencies
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/agencies')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5"></path>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              BACK
            </button>
            <h1 className="page-title">AGENCY PROFILE</h1>
            <p className="page-subtitle">{agency?.agency_code}</p>
          </div>
          <div className="header-right">
            <button className="btn-secondary" onClick={() => navigate(`/agencies/${id}/edit`)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              EDIT
            </button>
            <button className="delete-btn" onClick={handleDelete}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              DELETE
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {error && <div className="form-error">{error}</div>}

          <div className="profile-grid">
            {/* Left Column - Main Info */}
            <div className="profile-main">
              <div className="profile-card">
                <div className="profile-header">
                  <div className="agency-avatar">
                    {agency?.agency_name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="agency-title">
                    <h2>{agency?.agency_name}</h2>
                    <span className="agency-code">{agency?.agency_code}</span>
                  </div>
                </div>

                <div className="info-section">
                  <h3 className="section-title">CONTACT INFORMATION</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Email</span>
                      <span className="info-value">{agency?.email || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Phone</span>
                      <span className="info-value">
                        {agency?.phone_country_code && agency?.phone_number
                          ? `${agency.phone_country_code} ${agency.phone_number}`
                          : '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <h3 className="section-title">ADDRESS</h3>
                  <div className="info-grid">
                    <div className="info-item full-width">
                      <span className="info-label">Street Address</span>
                      <span className="info-value">
                        {agency?.address_line_1 || '-'}
                        {agency?.address_line_2 && <><br />{agency.address_line_2}</>}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">City</span>
                      <span className="info-value">{agency?.city || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">State / Province</span>
                      <span className="info-value">{agency?.state_province || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Country</span>
                      <span className="info-value">{agency?.country || agency?.country_code || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Postal Code</span>
                      <span className="info-value">{agency?.postal_code || '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="info-section">
                  <h3 className="section-title">BUSINESS DETAILS</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Tax ID / VAT Number</span>
                      <span className="info-value">{agency?.tax_identification_number || '-'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Business Registration</span>
                      <span className="info-value">{agency?.business_registration_number || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Status & Meta */}
            <div className="profile-sidebar">
              <div className="profile-card">
                <h3 className="section-title">STATUS</h3>
                <div className="status-control">
                  <Select
                    options={statusOptions}
                    value={agency?.status}
                    onChange={handleStatusChange}
                    disabled={statusUpdating}
                  />
                </div>
                {agency?.status_remarks && (
                  <div className="status-remarks">
                    <span className="remarks-label">Remarks</span>
                    <p>{agency.status_remarks}</p>
                  </div>
                )}
              </div>

              <div className="profile-card">
                <h3 className="section-title">TIMELINE</h3>
                <div className="timeline">
                  <div className="timeline-item">
                    <span className="timeline-label">Created</span>
                    <span className="timeline-value">{formatDate(agency?.created_at)}</span>
                  </div>
                  <div className="timeline-item">
                    <span className="timeline-label">Last Updated</span>
                    <span className="timeline-value">{formatDate(agency?.updated_at)}</span>
                  </div>
                  {agency?.approved_at && (
                    <div className="timeline-item">
                      <span className="timeline-label">Approved</span>
                      <span className="timeline-value">{formatDate(agency.approved_at)}</span>
                    </div>
                  )}
                  {agency?.approved_by_name && (
                    <div className="timeline-item">
                      <span className="timeline-label">Approved By</span>
                      <span className="timeline-value">{agency.approved_by_name}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgencyView;
